<template>
    <div class="WizardDataset">
        <QStepper
            v-if="(steps || []).length"
            v-model="step"
            alternativeLabels
            animated
            flat
        >
            <QStep
                v-for="(item, index) in steps"
                :key="`WizardDatasetStep-${index}`"
                :name="item.name"
                :title="item.title"
                :icon="item.icon"
                :done="currentIndex > index"
                :headerNav="currentIndex > index"
            >
                <Component
                    :is="item.component"
                    v-if="index === steps.length - 1"
                    :ref="item.name"
                    v-model="data"
                    :projectId="projectId"
                    :datasetId="(data || {}).datasetId"
                    :steps="steps"
                    :isLoadingRelevant="isLoadingRelevant"
                />
                <Component
                    :is="item.component"
                    v-else
                    :ref="item.name"
                    v-model="data"
                    :projectId="projectId"
                    :datasetId="(data || {}).datasetId"
                    :steps="steps"
                />
            </QStep>
        </QStepper>
    </div>
</template>
<script>
/* eslint-disable consistent-return */
import VueTypes from 'vue-types'
import cloneDeep from 'lodash/cloneDeep'
import {
    Api, notifyError, notifySuccess, apiRequest,
} from '@/api'
import { wizardMixin } from '@/mixins'
import { WIZARD } from '@/entities'
import Steps from './Steps'
import Setup, { WIZARD_INITAL_STATE } from './setup'

export default {
    name: 'WizardDataset',
    components: Steps,
    mixins: [wizardMixin],
    props: {
        projectId: VueTypes.string.isRequired,
        storageKey: VueTypes.string.def(WIZARD.KEYS.DATASET),
    },
    wizard() {
        const { config, initialState, mocks } = Setup()

        return {
            config,
            data: cloneDeep(initialState),
            mocks,
        }
    },
    data() {
        return {
            isLoadingRelevant: false,
            configureParams: {},

            isRouteHydrated: false,
        }
    },
    watch: {
        step: {
            handler() {
                this.syncDatasetNameToRoute()
            },
            immediate: true,
        },
        'data.settings.name': {
            handler() {
                this.syncDatasetNameToRoute()
            },
        },
    },
    mounted() {
        const { datasetId, step, datasetName } = this.$route.query || {}

        // If we are resuming another dataset, clear persisted wizard data to avoid mixing.
        if (datasetId && datasetId !== this.data?.datasetId) {
            const nextData = cloneDeep(WIZARD_INITAL_STATE)
            if (datasetName) nextData.settings = { ...(nextData.settings || {}), name: datasetName }
            this.data = { ...nextData, datasetId }
            this.setStorageWizard({ config: this.config, data: this.data })
        } else if (datasetId && !this.data?.datasetId) {
            this.setData({ datasetId })
        }

        // If route provides a name, trust it to avoid stale persisted names.
        if (datasetName && datasetName !== this.data?.settings?.name) {
            this.setData({ settings: { ...(this.data?.settings || {}), name: datasetName } })
        }

        const availableSteps = (this.steps || []).map((s) => s.name)
        if (step && availableSteps.includes(step) && step !== this.step) {
            this.setWizard({ step })
        }

        // Enable syncing after we've applied route-driven datasetId/name.
        this.isRouteHydrated = true
        this.syncDatasetNameToRoute()
    },
    methods: {
        syncDatasetNameToRoute() {
            if (!this.isRouteHydrated) return

            // Only show the dataset name in the wizard title for upload/preview steps.
            if (this.$route?.name !== 'WizardDataset') return
            if (!['upload', 'keys'].includes(this.step)) return

            const name = this.data?.settings?.name
            if (!name) return

            const nextQuery = { ...(this.$route.query || {}), datasetName: name }
            if (nextQuery.datasetName === this.$route.query.datasetName) return
            this.$router.replace({
                name: this.$route.name,
                params: this.$route.params,
                query: nextQuery,
            })
        },
        async exit() {
            const { datasetId } = this.data

            if (datasetId) {
                const { ok } = await this.removeDataset(datasetId)
                if (ok) this.finish()
            } else {
                this.finish()
            }
        },
        async removeDataset(datasetId) {
            return Api().datasets.delete({ datasetId })
                .then(() => ({ ok: true }))
                .catch(notifyError)
        },
        async submitStep0() {
            const isValid = await this.component.isValid()
            if (isValid) {
                const { settings = {} } = this.data
                return Api().datasets.create({ projectUid: this.projectId, ...settings })
                    .then(({ headers, response }) => {
                        if (headers) {
                            notifySuccess(this.$t('projects.datasets.notifications.created.text'))
                            return { ok: true, data: { datasetId: headers['x-location-uid'] } }
                        }

                        notifyError({ result: { response } })
                        return { ok: false }
                    })
                    .catch(notifyError)
            }
        },
        async submitStep1() {
            const isValid = await this.component.isValid()
            if (isValid) return { ok: true }
        },
        async submitStep2() {
            const isValid = await this.component.isValid()

            // Si isValid es un objeto (como en el antiguo submitStep3), comprobamos los checks
            let checkStringType = true

            if (typeof isValid === 'object') {
                checkStringType = isValid.checkStringType !== false
            }

            const errorMessages = []

            // Validación de campos clave (como antes)
            if (isValid === false || (typeof isValid === 'boolean' && !isValid)) {
                errorMessages.push(this.$t('wizard.notifications.keyFieldsError.text'))
            }

            // Validación de tipos relevantes
            if (!checkStringType) {
                errorMessages.push(this.$t('wizard.notifications.keyRelevantFieldsError.text'))
            }

            if (errorMessages.length > 0) {
                errorMessages.forEach((m) => {
                    notifyError({
                        result: {
                            response: {
                                data: { errorMessage: m },
                                status: 400,
                            },
                        },
                    })
                })
                return
            }

            // Si todo es válido, preparamos los parámetros para configurar el dataset
            const { config } = this.data
            const {
                activity, traceId, start, end,
            } = config || {}

            if (config.modifiedTypes) {
                const params = {
                    name: 'default',
                    activityColumn: (activity || {}).value,
                    startTimeColumn: (start || {}).value,
                    traceColumn: (traceId || {}).value,
                    ...(end ? { endTimeColumn: end.value } : {}),
                }
                const formattedColumns = config.modifiedTypes.map((c) => {
                    let { thousandCharacter } = config
                    let { decimalCharacter } = config

                    if (!thousandCharacter && !decimalCharacter) {
                        thousandCharacter = ','
                        decimalCharacter = '.'
                    } else if (!thousandCharacter) {
                        thousandCharacter = decimalCharacter === ',' ? '.' : ','
                    } else if (!decimalCharacter) {
                        decimalCharacter = thousandCharacter === '.' ? ',' : '.'
                    }

                    return {
                        ...c,
                        format: c.type === 'DECIMAL'
                            ? `${thousandCharacter}${decimalCharacter}`
                            : c.format,
                    }
                })
                this.configureParams = { ...params, columns: formattedColumns, homogenizeAttr: config.homogenizeAttr }
                const isFinished = await this.fetchConfigureAndFinish({ datasetId: this.data?.datasetId, params: this.configureParams }, config)
                if (isValid && isFinished) {
                    return { ok: true }
                }
            } else {
                notifyError({
                    result: {
                        response: {
                            data: { errorMessage: this.$t('wizard.notifications.keyRelevantFieldsLoading.text') },
                            status: 400,
                        },
                    },
                })
            }
        },
        finish() {
            const { projectId } = this.$route.params
            this.$router.push({ name: 'ProjectDatasets', params: { projectId } }).then(this.resetWizard)
        },
        async fetchConfigureAndFinish({ datasetId, params }) {
            this.isLoadingRelevant = true
            Api().datasets.configure({ datasetId, params })
                .then(() => {
                    apiRequest(Api().datasets.finishUpload({ datasetId }))
                        .then(() => {
                            notifySuccess(`${this.$t('wizard.notifications.keyFieldsSuccess.text')} 
                            ${this.$t('wizard.notifications.launched.dataset.text')}`)
                            this.finish()
                        })
                        .catch((e) => {
                            notifyError(e)
                            this.isLoadingRelevant = false
                            this.setWizard({ isNavigate: false })
                        })
                })
                // revisar -> no setWizard
                .catch((e) => {
                    notifyError(e)
                    this.isLoadingRelevant = false
                    this.setWizard({ isNavigate: false })
                })
        },
    },
}
</script>
<style>
.q-stepper--horizontal .q-stepper__step-inner {
    padding-bottom: 5px !important;
}
</style>
